<script setup lang="ts">
import { PaymentIntent } from '@stripe/stripe-js/dist/api'
import { Appearance, Stripe, StripeElements } from '@stripe/stripe-js/dist/stripe-js'
import Skeleton from 'pejer/src/components/ui/skeleton/Skeleton.vue'
import { nextTick, onMounted, ref, watch } from 'vue'

const props = defineProps<{
  stripe: Stripe | null
  clientSecret: string
  paymentReferenceID: string
  grossAmount: number
  currency: string
  googlePayEnabled: boolean
  applePayEnabled: boolean
}>()

const emits = defineEmits<{
  (e: 'onPaidSuccess', value: PaymentIntent): void
}>()

const directPaymentIsEnabled = ref<boolean | null>(null)
const elements = ref<StripeElements | null>(null)

// Google Pay or Apple Pay button
const directPayHTMLElement = ref<HTMLElement | null>(null)

const errorMessage = ref<string | null>(null)

watch(
  () => props.stripe,
  async () => {
    initializeStripe()
  }
)
onMounted(() => {
  initializeStripe()
})

async function initializeStripe() {
  if (!props.stripe) {
    elements.value = null
    return
  }

  // We need to wait a tick before continuing, as the HTML elements are not yet mounted inside the DataStateDisplay component
  await nextTick()

  const appearance: Appearance = {
    theme: 'stripe',
  }
  elements.value = props.stripe.elements({ appearance, clientSecret: props.clientSecret })

  await initializeDirectPayButton()
}

// Using Stripe's Payment Request Button for Apple Pay- & Google Pay-integration
async function initializeDirectPayButton() {
  const paymentRequest = props.stripe!.paymentRequest({
    country: 'NO',
    currency: props.currency.toLowerCase(),
    total: { label: 'Sum', amount: props.grossAmount },
    requestPayerName: true,
    requestPayerEmail: true,
  })

  const result = (await paymentRequest.canMakePayment()) as {
    applePay: boolean
    googlePay: boolean
  } | null
  if (
    (result?.googlePay && props.googlePayEnabled) ||
    (result?.applePay && props.applePayEnabled)
  ) {
    const prButton = elements.value!.create('paymentRequestButton', {
      paymentRequest,
      style: {
        paymentRequestButton: {
          type: 'buy', // shows “Buy with  Pay”
          theme: 'light',
          height: '58px',
        },
      },
    })

    paymentRequest.on('paymentmethod', async (ev) => {
      const { error, paymentIntent } = await props.stripe!.confirmCardPayment(
        props.clientSecret,
        {
          payment_method: ev.paymentMethod.id,
        },
        { handleActions: false }
      )
      errorMessage.value = error?.message ?? null

      if (!error) emits('onPaidSuccess', paymentIntent)
    })
    prButton.mount(directPayHTMLElement.value!)

    directPaymentIsEnabled.value = true
  } else {
    directPaymentIsEnabled.value = false
  }
}
</script>
<template>
  <div
    v-if="directPaymentIsEnabled == null || directPaymentIsEnabled === true"
    class="p-0 flex flex-col items-center justify-center w-full h-[50px] border border-slate-300 overflow-hidden hover:shadow-sm rounded-sm"
    :class="{
      hidden: directPaymentIsEnabled === null,
    }"
  >
    <!-- <Skeleton :class="cn('w-full h-full min-h-full', directPaymentIsEnabled ? 'hidden' : '')" /> -->
    <div
      class="justify-center flex flex-col h-full w-full hover:bg-gray-100/70 -mt-2"
      :class="{
        hidden: !directPaymentIsEnabled,
      }"
    >
      <div class="h-full w-full" ref="directPayHTMLElement"></div>
    </div>
    <div v-if="errorMessage" class="text-red-500 text-sm px-4 pb-2">
      {{ errorMessage }}
    </div>
  </div>
  <Skeleton v-if="directPaymentIsEnabled === null" class="h-[50px] w-full" />
</template>
